import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

/**
 * Phase 9 — section-scoped "Fill with AI" button rendered inside the
 * CollapsibleSection header. Lives next to the section title.
 *
 * Flow:
 *   1. Confirm modal: warns about overwriting current section values, offers
 *      a "Use saved sources" toggle (if the entity has cached raw_pages).
 *   2. While submitting: shows a 2-stage spinner (crawling → generating).
 *   3. On success: calls ``onDraft(draftPatch)`` so the parent merges the
 *      returned fields into local form state. Nothing is persisted server-side
 *      — the user still needs to press Save.
 */
export function SectionAutofillButton({
  entityType, // "brand" | "product"
  entityId,
  sectionKey, // see BRAND_SECTIONS / PRODUCT_SECTIONS in backend
  sectionLabel, // localized human label for the section
  testIdPrefix,
  hasCachedSources = false,
  onDraft,
  disabled = false,
}) {
  const { t } = useTranslation(["autofill", "common", "errors"]);
  const [open, setOpen] = useState(false);
  const [reuse, setReuse] = useState(hasCachedSources);
  const [stage, setStage] = useState(null); // null | "crawl" | "generate"

  const reset = () => {
    setStage(null);
    setReuse(hasCachedSources);
  };

  const onOpenChange = (next) => {
    if (stage) return; // don't allow closing while running
    setOpen(next);
    if (!next) reset();
  };

  const handleSubmit = async () => {
    setStage("crawl");
    const endpoint =
      entityType === "brand"
        ? `/brands/${entityId}/autofill-section`
        : `/products/${entityId}/autofill-section`;
    try {
      // Visually advance from crawl → generate ~1.6s in so the user sees the
      // 2-stage indicator even if the round-trip is fast.
      const stageTimer = setTimeout(() => setStage("generate"), 1600);
      const { data } = await api.post(endpoint, {
        section: sectionKey,
        reuse_sources: reuse,
      });
      clearTimeout(stageTimer);
      const draft = data?.draft || {};
      const filledCount = Object.keys(draft).filter((k) => {
        const v = draft[k];
        return v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0);
      }).length;
      onDraft?.(draft);
      toast.success(
        t("autofill:sectionFill.toast", {
          section: sectionLabel,
          count: filledCount,
          defaultValue: `Drafted ${filledCount} field(s) for ${sectionLabel}.`,
        }),
      );
      setOpen(false);
      reset();
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      const messageKey = err?.response?.data?.message_key;
      let msg = formatApiError(err, t("errors:generic"));
      if (messageKey) {
        msg = t(messageKey, { defaultValue: msg });
      } else if (typeof detail === "string" && detail) {
        msg = t(`errors:${detail}`, { defaultValue: detail });
      }
      toast.error(msg);
      setStage(null);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={disabled}
        data-testid={`${testIdPrefix}-ai-fill-trigger`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
        className="h-7 gap-1 px-2 text-[11px] font-medium text-indigo-700 hover:bg-indigo-50 hover:text-indigo-900"
      >
        <Sparkles className="h-3 w-3" strokeWidth={2} />
        {t("autofill:sectionFill.button", { defaultValue: "Fill with AI" })}
      </Button>

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          data-testid={`${testIdPrefix}-ai-fill-modal`}
          className="sm:max-w-md"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-lg font-semibold tracking-tight">
              <Sparkles className="h-4 w-4 text-indigo-600" strokeWidth={2} />
              {t("autofill:sectionFill.title", {
                section: sectionLabel,
                defaultValue: `Fill "${sectionLabel}" with AI`,
              })}
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              {t("autofill:sectionFill.subtitle", {
                section: sectionLabel,
                defaultValue: `The AI will regenerate just the "${sectionLabel}" fields. Existing values in this section will be replaced when you click Apply.`,
              })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900">
              {t("autofill:sectionFill.overwriteWarning", {
                defaultValue:
                  "Heads up — anything you've already typed in this section will be overwritten in the form. Nothing is saved until you press Save on the section.",
              })}
            </div>

            <div className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
              <div className="space-y-0.5">
                <Label className="text-xs font-semibold tracking-tight text-slate-700">
                  {t("autofill:sectionFill.reuseLabel", {
                    defaultValue: "Use saved sources",
                  })}
                </Label>
                <p className="text-[11px] text-slate-500">
                  {hasCachedSources
                    ? t("autofill:sectionFill.reuseHint", {
                        defaultValue:
                          "Skip crawling. Re-use the pages cached on this entity from the original AI extraction.",
                      })
                    : t("autofill:sectionFill.reuseUnavailable", {
                        defaultValue:
                          "No cached sources on this entity — we'll crawl fresh.",
                      })}
                </p>
              </div>
              <Switch
                disabled={!hasCachedSources || !!stage}
                checked={reuse}
                onCheckedChange={setReuse}
                data-testid={`${testIdPrefix}-ai-fill-reuse-toggle`}
              />
            </div>

            {stage ? (
              <StageStrip stage={stage} t={t} />
            ) : null}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={!!stage}
              data-testid={`${testIdPrefix}-ai-fill-cancel`}
            >
              {t("common:cancel")}
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={!!stage}
              data-testid={`${testIdPrefix}-ai-fill-submit`}
              className="bg-slate-900 text-white hover:bg-slate-800"
            >
              {stage ? (
                <>
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  {t(`autofill:sectionFill.stage.${stage}`, {
                    defaultValue: stage,
                  })}
                </>
              ) : (
                <>
                  <Sparkles className="mr-1 h-4 w-4" strokeWidth={2} />
                  {t("autofill:sectionFill.applyButton", {
                    defaultValue: "Generate & apply",
                  })}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function StageStrip({ stage, t }) {
  const stages = ["crawl", "generate"];
  return (
    <ul
      className="grid grid-cols-2 gap-2 rounded-xl bg-slate-50 px-3 py-3"
      data-testid="section-ai-fill-stages"
    >
      {stages.map((s) => {
        const active = stage === s;
        const done = stages.indexOf(s) < stages.indexOf(stage);
        return (
          <li
            key={s}
            data-testid={`section-ai-fill-stage-${s}${active ? "-active" : ""}`}
            className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs ${
              active
                ? "border border-indigo-200 bg-indigo-50 text-indigo-700"
                : done
                  ? "border border-emerald-200 bg-emerald-50 text-emerald-700"
                  : "border border-slate-200 bg-white text-slate-500"
            }`}
          >
            {active ? (
              <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} />
            ) : (
              <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
            )}
            <span className="font-medium">
              {t(`autofill:sectionFill.stage.${s}`, {
                defaultValue: s,
              })}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
